import React, { useState } from 'react';
import { 
  Award, 
  Shield, 
  Briefcase, 
  Building2, 
  Plane, 
  Rocket,
  CheckCircle2,
  Quote
} from 'lucide-react';

const FounderSection = () => {
  const [more, setMore] = useState(false);
  const extra = more ? '' : 'hidden sm:block';
  const credentials = [
    { label: 'CMII/PIC', desc: 'Certified Configuration Management Professional' },
    { label: 'PMP', desc: 'Project Management Professional' },
    { label: 'Security+', desc: 'CompTIA Security+ Certified' }
  ];

  const experience = [
    { company: 'NASA', icon: Rocket },
    { company: 'Boeing', icon: Plane },
    { company: 'Raytheon', icon: Shield },
    { company: 'CAE', icon: Building2 }
  ];

  return (
    <section className="py-12 md:py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" id="founder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-4">
            Leadership
          </span>
          <h2 className="text-4xl font-heading font-bold text-white mb-4">
            About the Founder
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Built from 15+ years of hands-on experience in aerospace, defense, and advanced manufacturing
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Image and Quick Facts */}
          <div className="space-y-6">
            {/* Founder Image */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-slate-800 rounded-2xl overflow-hidden border border-slate-700">
                <img 
                  src="https://customer-assets-v7afamib.emergentagent.net/job_397438fc-52a3-4e4c-8aa0-250fdeb6143e/artifacts/6fc30b4ba7ab7c60_CE6F0003-4A4B-44C1-81CE-D5D66B53890D.png"
                  alt="Founder of BaseLinx PLM"
                  className="w-full h-auto max-h-[420px] sm:max-h-none object-cover object-top"
                  data-testid="founder-image"
                />
              </div>
            </div>

            {/* Credentials */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-cyan-400" />
                Professional Certifications
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {credentials.map((cred, idx) => (
                  <div key={cred?.id ?? cred?.partNumber ?? cred?.ecoNumber ?? cred?.uuid ?? cred?.key ?? cred?.name ?? cred?.title ?? cred?.label ?? `idx-${idx}`} className="text-center p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    <div className="text-cyan-400 font-bold text-lg">{cred.label}</div>
                    <div className="text-gray-500 text-xs mt-1">{cred.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Experience Logos */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-cyan-400" />
                Industry Experience
              </h3>
              <div className="flex flex-wrap gap-3">
                {experience.map((exp, idx) => {
                  const Icon = exp.icon;
                  return (
                    <div key={exp?.id ?? exp?.partNumber ?? exp?.ecoNumber ?? exp?.uuid ?? exp?.key ?? exp?.name ?? exp?.title ?? exp?.label ?? `idx-${idx}`} className="flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700">
                      <Icon className="w-4 h-4 text-cyan-400" />
                      <span className="text-white text-sm font-medium">{exp.company}</span>
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700">
                  <Plane className="w-4 h-4 text-cyan-400" />
                  <span className="text-white text-sm font-medium">Southwest Airlines</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700">
                  <Plane className="w-4 h-4 text-cyan-400" />
                  <span className="text-white text-sm font-medium">United Airlines</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700">
                  <Building2 className="w-4 h-4 text-cyan-400" />
                  <span className="text-white text-sm font-medium">Juul Labs</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Story */}
          <div className="space-y-6">
            {/* Quote */}
            <div className="relative">
              <Quote className="absolute -top-2 -left-2 w-12 h-12 text-cyan-500/20" />
              <blockquote className="pl-8 text-xl text-gray-300 italic leading-relaxed">
                "Organizations rely on disconnected systems, unclear ownership, and inconsistent processes to manage highly complex products — and it doesn't scale. BaseLinx was created to change that."
              </blockquote>
            </div>

            {/* Main Story */}
            <div className="space-y-4 text-gray-400 leading-relaxed">
              <p>
                I'm the founder of BaseLinx, built from firsthand experience solving one of the most persistent challenges in aerospace, defense, and advanced manufacturing — <span className="text-white font-semibold">the lack of true configuration control across the product lifecycle</span>.
              </p>
              
              <p className={extra}>
                As a Configuration Management and Digital Engineering leader with over <span className="text-cyan-400 font-semibold">15 years of experience</span>, I've worked at the intersection of engineering, manufacturing, and operations, where small breakdowns in data, process, and ownership quickly become costly system-level failures.
              </p>

              <p className={extra}>
                My background spans <span className="text-white">NASA, Boeing (Commercial, Defense, and Special Projects), Raytheon, and CAE</span>, where I supported complex programs including F-16 flight simulators, as well as aviation leaders such as Southwest Airlines and Continental/United Airlines. I've also applied configuration discipline in high-growth commercial environments like Juul Labs.
              </p>

              <p className={extra}>
                Across these roles, I consistently saw the same problem: organizations rely on disconnected systems, unclear ownership, and inconsistent processes to manage highly complex products — and it doesn't scale.
              </p>
              <button onClick={() => setMore((m) => !m)} className="sm:hidden text-cyan-400 text-sm font-semibold" data-testid="founder-read-more">
                {more ? 'Show less' : 'Read full story'}
              </button>
            </div>

            {/* Mission Statement */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-500/20 p-6">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                The BaseLinx Mission
              </h3>
              <p className="text-gray-300 leading-relaxed">
                BaseLinx is an <span className="text-cyan-400 font-semibold">AI-enabled digital engineering and configuration intelligence platform</span> designed to bring structure, traceability, and control to the entire lifecycle — from requirements to design, manufacturing, and change management.
              </p>
            </div>

            {/* Focus Statement */}
            <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
              <p className="text-gray-300">
                <span className="text-white font-semibold">My focus is simple:</span> build solutions that make complex systems manageable, auditable, and scalable — not just theory, but <span className="text-cyan-400">practical, systems-driven approaches</span> to solving real operational problems.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;
